import React, { useState, useEffect } from "react";
import {
  FaBell,
  FaChartPie,
  FaArrowUp,
  FaArrowDown,
  FaCalendarAlt,
  FaUserCog,
} from "react-icons/fa";
import {
  MdPendingActions,
  MdDashboard,
  MdOutlineInsights,
} from "react-icons/md";
import {
  FileDoneOutlined,
  SolutionOutlined,
  SendOutlined,
  ExclamationCircleFilled,
  SafetyCertificateFilled,
  ProfileOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import axios from "../../services/axios";

const Dashboard = () => {
  // State values from API responses
  const [notifications, setNotifications] = useState([]);
  const user = localStorage.getItem("user");
  const projectName = JSON.parse(localStorage.getItem("Project"));
  const [pendingTasks, setPendingTasks] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [totalDocumentsApproved, setTotalDocumentsApproved] = useState(0);
  const [totalDocumentsRejected, setTotalDocumentsRejected] = useState(0);
  const [totalMasDocuments, setTotalMasDocuments] = useState(0);
  const [totalMasApproved, setTotalMasApproved] = useState(0);
  const [totalMasRejected, setTotalMasRejected] = useState(0);
  const [totalShopDocuments, setTotalShopDocuments] = useState(0);
  const [totalShopApproved, setTotalShopApproved] = useState(0);
  const [totalShopRejected, setTotalShopRejected] = useState(0);
  const role = JSON.parse(localStorage.getItem("role"))[0];

  // UI state
  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Track API call completion
  const [apiCallsCompleted, setApiCallsCompleted] = useState({
    notifications: false,
    pendingTasks: false,
    totalDocuments: false,
    documentsApproved: false,
    documentsRejected: false,
    masDocuments: false,
    masApproved: false,
    masRejected: false,
    shopDocuments: false,
    shopApproved: false,
    shopRejected: false,
  });

  // Dashboard summary calculations
  const totalApproved =
    totalDocumentsApproved + totalShopApproved + totalMasApproved;
  const totalRejected =
    totalDocumentsRejected + totalShopRejected + totalMasRejected;
  const totalUploaded = totalDocuments + totalShopDocuments + totalMasDocuments;

  // Refresh data handler
  const handleRefresh = () => {
    setIsLoading(true);
    setRefreshKey((prevKey) => prevKey + 1);
    setApiCallsCompleted({
      notifications: false,
      pendingTasks: false,
      totalDocuments: false,
      documentsApproved: false,
      documentsRejected: false,
      masDocuments: false,
      masApproved: false,
      masRejected: false,
      shopDocuments: false,
      shopApproved: false,
      shopRejected: false,
    });
  };

  // API calls from first code snippet
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.post("get-user-notifications", {
          user,
          projectName,
        });
        setNotifications(response.data.notifications.slice(0, 5));
        setApiCallsCompleted((prev) => ({ ...prev, notifications: true }));
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setApiCallsCompleted((prev) => ({ ...prev, notifications: true }));
      }
    };

    fetchNotifications();
  }, [user, projectName, refreshKey]);

  useEffect(() => {
    const fetchPendingTasks = async () => {
      try {
        const response = await axios.post("document-count", {
          user,
          role,
          projectName,
        });
        setPendingTasks(response.data.pendingCount);
        setApiCallsCompleted((prev) => ({ ...prev, pendingTasks: true }));
      } catch (error) {
        console.error("Error fetching pending tasks:", error);
        setApiCallsCompleted((prev) => ({ ...prev, pendingTasks: true }));
      }
    };

    fetchPendingTasks();
  }, [user, projectName, refreshKey]);

  useEffect(() => {}, [pendingTasks]);

  useEffect(() => {
    const fetchTotalDocuments = async () => {
      try {
        const response = await axios.post("get-total-docuemnts-uploaded", {
          user,
          role,
          projectName,
        });
        setTotalDocuments(response.data.totalDocuments);
        setApiCallsCompleted((prev) => ({ ...prev, totalDocuments: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, totalDocuments: true }));
      }
    };

    fetchTotalDocuments();
  }, [user, role, projectName, refreshKey]);

  useEffect(() => {
    const fetchTotalDocumentsApproved = async () => {
      try {
        const response = await axios.post("get-docuemnts-approved", {
          user,
          role,
          projectName,
        });
        setTotalDocumentsApproved(response.data.totalDocumentsApproved);
        setApiCallsCompleted((prev) => ({ ...prev, documentsApproved: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, documentsApproved: true }));
      }
    };

    fetchTotalDocumentsApproved();
  }, [user, role, projectName, refreshKey]);

  useEffect(() => {
    const fetchTotalDocumentsRejected = async () => {
      try {
        const response = await axios.post("get-docuemnts-rejected", {
          user,
          role,
          projectName,
        });
        setTotalDocumentsRejected(response.data.totalDocumentsRejected);
        setApiCallsCompleted((prev) => ({ ...prev, documentsRejected: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, documentsRejected: true }));
      }
    };

    fetchTotalDocumentsRejected();
  }, [user, role, projectName, refreshKey]);

  useEffect(() => {
    const fetchTotalMasDocuments = async () => {
      try {
        const response = await axios.post("mas-total-docuemnts-uploaded", {
          user,
          role,
          projectName,
        });
        setTotalMasDocuments(response.data.totalMasDocuments);
        setApiCallsCompleted((prev) => ({ ...prev, masDocuments: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, masDocuments: true }));
      }
    };

    fetchTotalMasDocuments();
  }, [user, role, projectName, refreshKey]);

  useEffect(() => {
    const fetchTotalMasApproved = async () => {
      try {
        const response = await axios.post("mas-docuemnts-approved", {
          user,
          role,
          projectName,
        });
        setTotalMasApproved(response.data.totalMasApproved);
        setApiCallsCompleted((prev) => ({ ...prev, masApproved: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, masApproved: true }));
      }
    };

    fetchTotalMasApproved();
  }, [user, role, projectName, refreshKey]);

  useEffect(() => {
    const fetchTotalMasRejected = async () => {
      try {
        const response = await axios.post("mas-docuemnts-rejected", {
          user,
          role,
          projectName,
        });
        setTotalMasRejected(response.data.totalMasRejected);
        setApiCallsCompleted((prev) => ({ ...prev, masRejected: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, masRejected: true }));
      }
    };

    fetchTotalMasRejected();
  }, [user, role, projectName, refreshKey]);

  useEffect(() => {
    const fetchTotalShopDocuments = async () => {
      try {
        const response = await axios.post("shop-total-docuemnts-uploaded", {
          user,
          role,
          projectName,
        });
        setTotalShopDocuments(response.data.totalShopDocuments);
        setApiCallsCompleted((prev) => ({ ...prev, shopDocuments: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, shopDocuments: true }));
      }
    };

    fetchTotalShopDocuments();
  }, [user, role, projectName, refreshKey]);

  useEffect(() => {
    const fetchTotalShopApproved = async () => {
      try {
        const response = await axios.post("shop-docuemnts-approved", {
          user,
          role,
          projectName,
        });
        setTotalShopApproved(response.data.totalShopApproved);
        setApiCallsCompleted((prev) => ({ ...prev, shopApproved: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, shopApproved: true }));
      }
    };

    fetchTotalShopApproved();
  }, [user, role, projectName, refreshKey]);

  useEffect(() => {
    const fetchTotalShopRejected = async () => {
      try {
        const response = await axios.post("shop-docuemnts-rejected", {
          user,
          role,
          projectName,
        });
        setTotalShopRejected(response.data.totalShopRejected);
        setApiCallsCompleted((prev) => ({ ...prev, shopRejected: true }));
      } catch (error) {
        console.error("Error fetching total documents:", error);
        setApiCallsCompleted((prev) => ({ ...prev, shopRejected: true }));
      }
    };

    fetchTotalShopRejected();
  }, [user, role, projectName, refreshKey]);

  // Check if all API calls are completed to toggle loading state
  useEffect(() => {
    const allCompleted = Object.values(apiCallsCompleted).every(
      (completed) => completed
    );
    if (allCompleted) {
      setIsLoading(false);
    }
  }, [apiCallsCompleted]);

  // Prepare chart data
  const totalDocumentsPieData = [
    { name: "Total Uploaded", value: totalDocuments, color: "#3B82F6" },
    { name: "Approved", value: totalDocumentsApproved, color: "#10B981" },
    { name: "Rejected", value: totalDocumentsRejected, color: "#EF4444" },
  ];

  const shopDrawingsPieData = [
    { name: "Total Uploaded", value: totalShopDocuments, color: "#3B82F6" },
    { name: "Approved", value: totalShopApproved, color: "#10B981" },
    { name: "Rejected", value: totalShopRejected, color: "#EF4444" },
  ];

  const masPieData = [
    { name: "Total Uploaded", value: totalMasDocuments, color: "#3B82F6" },
    { name: "Approved", value: totalMasApproved, color: "#10B981" },
    { name: "Rejected", value: totalMasRejected, color: "#EF4444" },
  ];

  const barChartData = [
    {
      name: "Total Documents",
      Uploaded: totalDocuments,
      Approved: totalDocumentsApproved,
      Rejected: totalDocumentsRejected,
    },
    {
      name: "Shop Drawings",
      Uploaded: totalShopDocuments,
      Approved: totalShopApproved,
      Rejected: totalShopRejected,
    },
    {
      name: "MAS",
      Uploaded: totalMasDocuments,
      Approved: totalMasApproved,
      Rejected: totalMasRejected,
    },
  ];

  // Loading placeholder
  if (isLoading) {
    return (
      <div className="relative flex-1 p-10 overflow-y-auto ml-7 mr-2 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-700">
            Loading dashboard data...
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex-1 p-6 overflow-y-auto ml-7 mr-2 min-h-screen bg-gray-50">
      {/* Header with project name and refresh button */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button
            onClick={handleRefresh}
            className="flex items-center bg-white hover:bg-blue-50 text-blue-700 font-medium py-2 px-4 rounded-lg shadow"
          >
            <ReloadOutlined className="mr-2" />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex mb-6 bg-white rounded-lg shadow overflow-x-auto">
        <button
          className={`px-4 py-3 flex items-center ${
            activeTab === "overview"
              ? "text-blue-600 border-b-2 border-blue-600 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("overview")}
        >
          <MdDashboard className="mr-2" /> Overview
        </button>
        <button
          className={`px-4 py-3 flex items-center ${
            activeTab === "documents"
              ? "text-blue-600 border-b-2 border-blue-600 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("documents")}
        >
          <FileDoneOutlined className="mr-2" /> Documents
        </button>
        <button
          className={`px-4 py-3 flex items-center ${
            activeTab === "shop"
              ? "text-blue-600 border-b-2 border-blue-600 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("shop")}
        >
          <SolutionOutlined className="mr-2" /> Shop Drawings
        </button>
        <button
          className={`px-4 py-3 flex items-center ${
            activeTab === "mas"
              ? "text-blue-600 border-b-2 border-blue-600 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("mas")}
        >
          <ProfileOutlined className="mr-2" /> MAS
        </button>
        <button
          className={`px-4 py-3 flex items-center ${
            activeTab === "charts"
              ? "text-blue-600 border-b-2 border-blue-600 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("charts")}
        >
          <FaChartPie className="mr-2" /> Charts
        </button>
      </div>

      {/* Overview Tab Content */}
      {activeTab === "overview" && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-blue-100">Total Uploads</p>
                  <h3 className="text-3xl font-bold mt-1">{totalUploaded}</h3>
                </div>
                <div className="p-3 bg-blue-400 bg-opacity-30 rounded-full">
                  <FileDoneOutlined className="text-2xl" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <FaCalendarAlt className="mr-1" />
                <span>All Documents</span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-green-100">Approved</p>
                  <h3 className="text-3xl font-bold mt-1">{totalApproved}</h3>
                </div>
                <div className="p-3 bg-green-400 bg-opacity-30 rounded-full">
                  <SafetyCertificateFilled className="text-2xl" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                {totalApproved > 0 ? (
                  <>
                    <FaArrowUp className="mr-1" />
                    <span>
                      {Math.round((totalApproved / totalUploaded) * 100)}%
                      approval rate
                    </span>
                  </>
                ) : (
                  <span>No approvals yet</span>
                )}
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-red-100">Rejected</p>
                  <h3 className="text-3xl font-bold mt-1">{totalRejected}</h3>
                </div>
                <div className="p-3 bg-red-400 bg-opacity-30 rounded-full">
                  <ExclamationCircleFilled className="text-2xl" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                {totalRejected > 0 ? (
                  <>
                    <FaArrowDown className="mr-1" />
                    <span>
                      {Math.round((totalRejected / totalUploaded) * 100)}%
                      rejection rate
                    </span>
                  </>
                ) : (
                  <span>No rejections</span>
                )}
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg shadow p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-yellow-100">Pending Tasks</p>
                  <h3 className="text-3xl font-bold mt-1">{pendingTasks}</h3>
                </div>
                <div className="p-3 bg-yellow-400 bg-opacity-30 rounded-full">
                  <MdPendingActions className="font-bold text-2xl" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <FaUserCog className="mr-1" />
                <span>Requiring action</span>
              </div>
            </div>
          </div>

          {/* Charts and Notifications */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Status Summary Chart */}
            <div className="bg-white rounded-lg shadow p-6 col-span-2">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Document Status Summary
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Uploaded" fill="#3B82F6" />
                  <Bar dataKey="Approved" fill="#10B981" />
                  <Bar dataKey="Rejected" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Recent Notifications
              </h2>
              {notifications.length > 0 ? (
                <div className="space-y-4">
                  {notifications.map((notif, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-blue-50 rounded-lg"
                    >
                      <div className="bg-blue-100 p-2 rounded-full mr-3">
                        <FaBell className="text-blue-500" />
                      </div>
                      <div>
                        <p className="text-gray-800">{notif.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {notif.timestamp
                            ? new Date(notif.timestamp).toLocaleString()
                            : "Recently"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-56 text-gray-500">
                  <FaBell className="text-4xl mb-2" />
                  <p>No new notifications</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Documents Tab Content */}
      {activeTab === "documents" && (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Documents Overview
            </h2>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/3">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={totalDocumentsPieData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {totalDocumentsPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="lg:w-2/3 mt-4 lg:mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <FileDoneOutlined className="text-blue-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Total Documents
                          </h3>
                          <p className="text-3xl font-bold text-blue-600">
                            {totalDocuments}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <SafetyCertificateFilled className="text-green-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Approved Documents
                          </h3>
                          <p className="text-3xl font-bold text-green-600">
                            {totalDocumentsApproved}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <ExclamationCircleFilled className="text-red-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Rejected Documents
                          </h3>
                          <p className="text-3xl font-bold text-red-600">
                            {totalDocumentsRejected}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <SendOutlined className="text-yellow-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Pending Review
                          </h3>
                          <p className="text-3xl font-bold text-yellow-600">
                            {totalDocuments -
                              (totalDocumentsApproved + totalDocumentsRejected)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Document Status Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <SolutionOutlined className="text-blue-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">Internal Review</h3>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">3 documents in progress</p>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <SendOutlined className="text-yellow-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">
                    Transmitted for Approval
                  </h3>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">3 documents awaiting response</p>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <ProfileOutlined className="text-indigo-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">Engineer Review</h3>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">3 documents with engineer</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Shop Drawings Tab Content */}
      {activeTab === "shop" && (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Shop Drawings Overview
            </h2>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/3">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={shopDrawingsPieData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {shopDrawingsPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="lg:w-2/3 mt-4 lg:mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <FileDoneOutlined className="text-blue-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Total Shop Drawings
                          </h3>
                          <p className="text-3xl font-bold text-blue-600">
                            {totalShopDocuments}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <SafetyCertificateFilled className="text-green-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Approved Shop Drawings
                          </h3>
                          <p className="text-3xl font-bold text-green-600">
                            {totalShopApproved}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <ExclamationCircleFilled className="text-red-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Rejected Shop Drawings
                          </h3>
                          <p className="text-3xl font-bold text-red-600">
                            {totalShopRejected}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <SendOutlined className="text-yellow-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Pending Review
                          </h3>
                          <p className="text-3xl font-bold text-yellow-600">
                            {totalShopDocuments -
                              (totalShopApproved + totalShopRejected)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Shop Drawings Status Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <SolutionOutlined className="text-blue-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">Internal Review</h3>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">3 shop drawings in progress</p>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <SendOutlined className="text-yellow-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">
                    Transmitted for Approval
                  </h3>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">
                    3 shop drawings awaiting response
                  </p>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <ProfileOutlined className="text-indigo-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">Engineer Review</h3>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">3 shop drawings with engineer</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* MAS Tab Content */}
      {activeTab === "mas" && (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              MAS Overview
            </h2>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/3">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={masPieData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {masPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="lg:w-2/3 mt-4 lg:mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <FileDoneOutlined className="text-blue-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Total MAS Documents
                          </h3>
                          <p className="text-3xl font-bold text-blue-600">
                            {totalMasDocuments}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <SafetyCertificateFilled className="text-green-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Approved MAS Documents
                          </h3>
                          <p className="text-3xl font-bold text-green-600">
                            {totalMasApproved}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <ExclamationCircleFilled className="text-red-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Rejected MAS Documents
                          </h3>
                          <p className="text-3xl font-bold text-red-600">
                            {totalMasRejected}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <SendOutlined className="text-yellow-600 text-2xl mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold">
                            Pending Review
                          </h3>
                          <p className="text-3xl font-bold text-yellow-600">
                            {totalMasDocuments -
                              (totalMasApproved + totalMasRejected)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              MAS Status Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <SolutionOutlined className="text-blue-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">Internal Review</h3>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">3 MAS documents in progress</p>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <SendOutlined className="text-yellow-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">
                    Transmitted for Approval
                  </h3>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">
                    3 MAS documents awaiting response
                  </p>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <ProfileOutlined className="text-indigo-600 text-3xl mr-4" />
                  <h3 className="text-xl font-semibold">Engineer Review</h3>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <p className="text-lg font-medium">Status</p>
                  <p className="text-gray-600">3 MAS documents with engineer</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Charts Tab Content */}
      {activeTab === "charts" && (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Comprehensive Charts
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Total Documents Pie Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Total Documents Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={totalDocumentsPieData}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {totalDocumentsPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Shop Drawings Pie Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Shop Drawings Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={shopDrawingsPieData}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {shopDrawingsPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* MAS Pie Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  MAS Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={masPieData}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {masPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Comparison Bar Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Document Types Comparison
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Uploaded" fill="#3B82F6" />
                    <Bar dataKey="Approved" fill="#10B981" />
                    <Bar dataKey="Rejected" fill="#EF4444" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Status Table */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Document Status Summary
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Document Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Uploaded
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Approved
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rejected
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pending
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Approval Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Documents
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {totalDocuments}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                        {totalDocumentsApproved}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                        {totalDocumentsRejected}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-medium">
                        {totalDocuments -
                          (totalDocumentsApproved + totalDocumentsRejected)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {totalDocuments > 0
                          ? `${Math.round(
                              (totalDocumentsApproved / totalDocuments) * 100
                            )}%`
                          : "0%"}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Shop Drawings
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {totalShopDocuments}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                        {totalShopApproved}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                        {totalShopRejected}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-medium">
                        {totalShopDocuments -
                          (totalShopApproved + totalShopRejected)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {totalShopDocuments > 0
                          ? `${Math.round(
                              (totalShopApproved / totalShopDocuments) * 100
                            )}%`
                          : "0%"}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        MAS
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {totalMasDocuments}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                        {totalMasApproved}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                        {totalMasRejected}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-medium">
                        {totalMasDocuments -
                          (totalMasApproved + totalMasRejected)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {totalMasDocuments > 0
                          ? `${Math.round(
                              (totalMasApproved / totalMasDocuments) * 100
                            )}%`
                          : "0%"}
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Total
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {totalUploaded}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-700">
                        {totalApproved}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-700">
                        {totalRejected}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-yellow-700">
                        {totalUploaded - (totalApproved + totalRejected)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {totalUploaded > 0
                          ? `${Math.round(
                              (totalApproved / totalUploaded) * 100
                            )}%`
                          : "0%"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;